import { useState, useCallback, useMemo, useContext } from 'react';
import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';

interface PopperTriggerLogicT {
  referenceElement: HTMLButtonElement | null;
  showTimePicker: boolean;
  closeTimePicker: () => void;
  setShowTimePicker: TypescriptHelpersT.StateSetter<boolean>;
  handleToggleTimePicker: () => void;
  handleSetTriggerRef: (instance: HTMLButtonElement) => void;
  handleMenuWrapperKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
}
export const usePopperTriggerLogic = (): PopperTriggerLogicT => {
  const {
    pickerButtonRef,
    props: { onPickerClose, onPickerOpen, disabled },
    isWithCalendarToo,
    trackFocusTimewheelCurrHour,
    trackFocusPicker,
    latestInteractionRegion,
    currFocusDescriber,
  } = useContext(ControlledDateTimePickerContext);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const closeTimePicker = useCallback(() => {
    setShowTimePicker(false);
    onPickerClose();
  }, [onPickerClose]);
  const handleToggleTimePicker = useCallback(() => {
    if (disabled) return;
    setShowTimePicker((oldShowCalendar) => {
      if (!oldShowCalendar) {
        onPickerOpen();
        if (!isWithCalendarToo) trackFocusTimewheelCurrHour();
      } else onPickerClose();
      return !oldShowCalendar;
    });
  }, [disabled, isWithCalendarToo, onPickerClose, onPickerOpen, trackFocusTimewheelCurrHour]);

  const handleSetTriggerRef = useCallback(
    (newRef: HTMLButtonElement) => {
      pickerButtonRef.current = newRef;
      setReferenceElement(newRef);

      if (latestInteractionRegion === 'picker-icon' && currFocusDescriber === 'time-picker-icon') newRef?.focus();
    },
    [currFocusDescriber, latestInteractionRegion, pickerButtonRef],
  );
  const handleMenuWrapperKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const { key } = e;
      if (key === 'Escape') {
        closeTimePicker();
        trackFocusPicker();
      }
    },
    [closeTimePicker, trackFocusPicker],
  );
  return useMemo(
    () => ({
      referenceElement,
      showTimePicker,
      setShowTimePicker,
      handleToggleTimePicker,
      handleSetTriggerRef,
      handleMenuWrapperKeyDown,
      closeTimePicker,
    }),
    [
      closeTimePicker,
      handleMenuWrapperKeyDown,
      handleSetTriggerRef,
      handleToggleTimePicker,
      referenceElement,
      showTimePicker,
    ],
  );
};
